import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, apiErrorMessage } from "@/lib/api";

const inputCls = "bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF]";

export default function ClientEditor({ open, onOpenChange, client, onSaved }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(client?.email || "");
    setName(client?.name || "");
    setPassword("");
  }, [client, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (client) {
        const payload = { name };
        if (password) payload.password = password;
        await adminApi.updateClient(client.user_id, payload);
      } else {
        await adminApi.createClient({ email, name, password });
      }
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white max-w-lg" data-testid="client-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{client ? "Edit client" : "Create client"}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {client ? "Update the client's display name or password." : "Create a new client account. They can log in with these credentials."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!client && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="client-input-email" className={`mt-1.5 ${inputCls}`} />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="client-input-name" className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">
              {client ? "New password (leave blank to keep)" : "Password"}
            </Label>
            <Input
              type="password"
              required={!client}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="client-input-password"
              className={`mt-1.5 ${inputCls}`}
              minLength={6}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="client-editor-cancel"
                    className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="client-editor-save"
                    className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">
              {saving ? "Saving..." : (client ? "Save changes" : "Create client")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
